// src/config.ts
import defaultsDeep from "lodash.defaultsdeep";
import isMatch from "lodash.ismatch";
import { InvalidConfigError } from "@dxos/protocols";
import { schema } from "@dxos/protocols/proto";
import { trace } from "@dxos/tracing";
import { getDeep, setDeep } from "@dxos/util";
function _ts_decorate(decorators, target, key, desc) {
  var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
  else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}
var configRootType = schema.getCodecForType("dxos.config.Config");
var mapFromKeyValues = (spec, values) => {
  const config = {};
  for (const [key, { path, type }] of Object.entries(spec)) {
    let value = values[key];
    if (value !== void 0) {
      if (type) {
        switch (type) {
          case "boolean": {
            value = String(value).toLowerCase() === "true" || value === "1";
            break;
          }
          case "number": {
            value = Number(value);
            break;
          }
          case "string": {
            break;
          }
          case "json": {
            value = value ? JSON.parse(value) : null;
            break;
          }
          default: {
            throw new Error(`Invalid type: ${type}`);
          }
        }
      }
      setDeep(config, path.split("."), value);
    }
  }
  return config;
};
var mapToKeyValues = (spec, values) => {
  const config = {};
  for (const [key, { path, type }] of Object.entries(spec)) {
    const value = getDeep(values, path.split("."));
    if (value !== void 0) {
      switch (type) {
        case "json":
          config[key] = JSON.stringify(value);
          break;
        default:
          config[key] = value;
      }
    }
  }
  return config;
};
var validateConfig = (config) => {
  if (!("version" in config)) {
    throw new InvalidConfigError({
      message: "Version not specified"
    });
  }
  if (config?.version !== 1) {
    throw new InvalidConfigError({
      message: `Invalid config version: ${config.version}`
    });
  }
  const error = configRootType.protoType.verify(config);
  if (error) {
    throw new InvalidConfigError({
      message: String(error)
    });
  }
  return config;
};
var ConfigResource = /* @__PURE__ */ Symbol.for("dxos.resource.Config");
var Config = class {
  _config;
  /**
  * Creates an immutable instance.
  * @constructor
  */
  constructor(config = {}, ...objects) {
    this._config = validateConfig(defaultsDeep(config, ...objects, {
      version: 1
    }));
  }
  /**
  * Returns an immutable config JSON object.
  */
  get values() {
    return this._config;
  }
  /**
  * Returns the given config property.
  *
  * @param key A key in the config object. Can be a nested property with keys separated by dots: 'services.signal.server'.
  * @param defaultValue Default value to return if option is not present in the config.
  * @returns The config value or undefined if the option is not present.
  */
  get(key, defaultValue) {
    return getDeep(this._config, key.split(".")) ?? defaultValue;
  }
  /**
  * Get unique key.
  */
  find(path, test) {
    const values = getDeep(this._config, path.split("."));
    if (!Array.isArray(values)) {
      return;
    }
    return values.find((value) => isMatch(value, test));
  }
  /**
  * Returns the given config property or throw if it doesn't exist.
  *
  * @param key A key in the config object. Can be a nested property with keys separated by dots: 'services.signal.server'.
  */
  getOrThrow(key) {
    const value = getDeep(this._config, key.split("."));
    if (!value) {
      throw new Error(`Config option not present: ${key}`);
    }
    return value;
  }
};
Config = _ts_decorate([
  trace.resource({
    annotation: ConfigResource
  })
], Config);

export {
  mapFromKeyValues,
  mapToKeyValues,
  validateConfig,
  ConfigResource,
  Config
};
//# sourceMappingURL=chunk-ZY4D4E3Q.mjs.map
